/**
 * Bookings exist only after a signed agreement and a succeeded payment webhook (phase 3). The tables
 * exist in phase 0, empty, so availability and the calendar can read them. `BookingSummary` is the
 * calendar's shape (phase 0); `Booking` completes it (12 §2) with the BEO, the Resy lock, the totals
 * snapshot and the balance schedule. Phase-3 entities carry `venueId` (server-bound, read-only).
 */
import { z } from 'zod';
import { Actor, Currency, DateString, Id, Instant, InstantRange, NonNegativeCents, TimeHHmm, Timestamps, } from './common.js';
import { AddonSelection, BalanceInstallment, Quote } from './quote.js';
export const BookingStatus = z.enum(['booked', 'balance_due', 'paid', 'completed', 'cancelled']);
export const ResyLockStatus = z.enum(['not_needed', 'pending', 'done', 'failed']);
export const BookingSummary = z.object({
    id: Id,
    leadId: Id,
    productId: Id,
    roomIds: z.array(Id),
    date: DateString,
    window: InstantRange,
    headcount: z.number().int(),
    status: BookingStatus,
    resyLock: ResyLockStatus,
    ...Timestamps,
});
// ── Totals and balance schedule (snapshotted from the quote at booking) ──
/** The quote's numbers frozen at the moment of booking; later pricing changes never touch them. */
export const BookingTotals = Quote.pick({
    pricingRuleId: true,
    pricingRuleVersion: true,
    lines: true,
    subtotalCents: true,
    minSpendShortfallCents: true,
    serviceChargeCents: true,
    taxCents: true,
    totalCents: true,
    depositDueCents: true,
    depositComponents: true,
    cardFee: true,
}).extend({ quoteId: Id, currency: Currency });
/**
 * - `scheduled` — not yet due;
 * - `processing` — an off-session charge is in flight;
 * - `link_sent` — the charge needed the payer or was declined; a payment link was emailed;
 * - `paid` / `failed`.
 */
export const InstallmentStatus = z.enum(['scheduled', 'processing', 'link_sent', 'paid', 'failed']);
/** One balance installment of a booking (past-due ones were folded into the deposit at quoting). */
export const BookingInstallment = BalanceInstallment.extend({
    /** A booking always has a date, so every installment has a due date. */
    dueDate: DateString,
    status: InstallmentStatus,
    /** The payment that settled (or last tried) it. */
    paymentId: Id.nullable(),
    paidAt: Instant.nullable(),
});
// ── BEO (02 §6.6, 12 §3.5) ────────────────────────────────────────────────
export const BeoMenuItem = z.object({
    label: z.string().trim().min(1).max(200),
    quantity: z.number().int().min(0).max(10_000).nullable(),
    notes: z.string().max(1000).nullable(),
});
export const BeoVendor = z.object({
    /** e.g. "Florist", "DJ", "Mashgiach". */
    role: z.string().trim().min(1).max(80),
    name: z.string().trim().max(200),
    /** Free text the vendor gave (phone or email). */
    contact: z.string().max(200).nullable(),
    arrival: TimeHHmm.nullable(),
    notes: z.string().max(1000).nullable(),
});
export const BeoTimelineItem = z.object({
    at: TimeHHmm,
    item: z.string().trim().min(1).max(300),
});
const beoFields = {
    eventName: z.string().max(200).nullable(),
    /** Space and room names as printed (snapshotted, so a later rename does not rewrite a sent BEO). */
    spaceName: z.string().max(200),
    roomNames: z.array(z.string().max(200)).max(20),
    date: DateString,
    /** Venue-local times. */
    setupFrom: TimeHHmm,
    start: TimeHHmm,
    end: TimeHHmm,
    teardownUntil: TimeHHmm,
    headcount: z.number().int().min(1).max(2000),
    menu: z.object({
        packageName: z.string().max(200).nullable(),
        items: z.array(BeoMenuItem).max(100),
        notes: z.string().max(4000),
    }),
    dietary: z.string().max(4000),
    allergies: z.string().max(4000),
    setupNotes: z.string().max(4000),
    vendors: z.array(BeoVendor).max(50),
    timeline: z.array(BeoTimelineItem).max(100),
    notes: z.string().max(10_000),
};
/** What staff edit (`PUT /s/bookings/:id/beo` replaces it whole); every edit re-renders the PDF. */
export const BeoInput = z.object(beoFields);
/** Generated at booking from the plan, quote and venue; stored as `booking.beo`. */
export const Beo = z.object({
    ...beoFields,
    generatedAt: Instant,
    updatedAt: Instant,
    updatedBy: Actor,
    /** Last "Send to kitchen" (or the automatic send after the Resy lock). */
    sentToKitchenAt: Instant.nullable(),
});
// ── Booking ───────────────────────────────────────────────────────────────
export const Booking = BookingSummary.extend({
    venueId: Id,
    /** The hold this booking converted. */
    holdId: Id.nullable(),
    agreementId: Id.nullable(),
    packageId: Id.nullable(),
    addons: z.array(AddonSelection),
    resyLockBy: Actor.nullable(),
    resyLockAt: Instant.nullable(),
    /** Null only until the generator has run. */
    beo: Beo.nullable(),
    finalHeadcount: z.number().int().min(1).max(2000).nullable(),
    /** Venue-local date the final headcount is due by (pricing rule, e.g. 7 days before). */
    finalHeadcountDueAt: DateString.nullable(),
    totals: BookingTotals,
    balanceSchedule: z.array(BookingInstallment),
    /** Succeeded deposit, balance and extra payments minus refunds. */
    paidCents: NonNegativeCents,
    /** `totals.totalCents − paidCents`, never negative. */
    outstandingCents: NonNegativeCents,
    /** A saved Stripe payment method exists, so "Charge now" can run off-session. Ids stay server-side. */
    cardOnFile: z.boolean(),
    cancelledAt: Instant.nullable(),
    cancelReason: z.string().max(500).nullable(),
});
/** What the guest's Booked scene shows. */
export const GuestBooking = Booking.pick({
    id: true,
    productId: true,
    roomIds: true,
    date: true,
    window: true,
    headcount: true,
    status: true,
    finalHeadcountDueAt: true,
    totals: true,
    balanceSchedule: true,
    paidCents: true,
    outstandingCents: true,
});
// ── Resy (05 §2): staff lock in Resy by hand; the covers are read-only ─────
export const ResyLockScope = z.enum(['rooms', 'whole_venue']);
/** `api` is reserved for when Resy writes are allowed (05 §2 "later"); nothing writes to Resy now. */
export const ResyLockMethod = z.enum(['manual', 'api']);
export const ResyLock = z.object({
    id: Id,
    venueId: Id,
    bookingId: Id,
    scope: ResyLockScope,
    /** Empty for `whole_venue`. */
    roomIds: z.array(Id),
    date: DateString,
    performedBy: Actor,
    method: ResyLockMethod,
    /** What staff did in Resy OS, in their words. */
    evidence: z.string().max(2000),
    performedAt: Instant,
    ...Timestamps,
});
/**
 * Resy's restaurant covers for a date, from the read-only port. `stale` when served from the cache
 * after a failed refresh. No credentials, or a failure with nothing cached → `503 RESY_UNAVAILABLE`.
 */
export const ResyCovers = z.object({
    date: DateString,
    covers: z.number().int().min(0),
    /** Per service when Resy gives it, e.g. `{ dinner: 112 }`. */
    byService: z.record(z.string().max(80), z.number().int().min(0)).optional(),
    fetchedAt: Instant,
    stale: z.boolean(),
});
//# sourceMappingURL=bookings.js.map